/** @format */

export type InvitationStatusColor =
	| "success"
	| "error"
	| "warning"
	| "info"
	| "default";

export interface InvitationStatusMeta {
	label: string;
	color: InvitationStatusColor;
	/** Plain-language explanation shown on cards that have no actions */
	helperText: string;
}

const STATUS_META: Record<string, InvitationStatusMeta> = {
	PENDING_INVITEE_ACTION: {
		label: "Pending",
		color: "warning",
		helperText: "Waiting for your response.",
	},
	ACCEPTED_AWAITING_VERIFICATION: {
		label: "Awaiting Verification",
		color: "info",
		helperText:
			"You accepted. Verify your identity to finish joining this circle.",
	},
	ACCEPTED_UNDER_REVIEW: {
		label: "Under Review",
		color: "info",
		helperText:
			"You accepted. The circle leader is reviewing your request.",
	},
	APPROVED_ADDING_MEMBER: {
		label: "Approved",
		color: "success",
		helperText: "You're approved. Your membership is being finalized.",
	},
	COMPLETED_MEMBER_ADDED: {
		label: "Member Added",
		color: "success",
		helperText: "You're a member of this circle.",
	},
	DENIED: {
		label: "Denied",
		color: "error",
		helperText: "You declined this invitation.",
	},
	EXPIRED: {
		label: "Expired",
		color: "error",
		helperText:
			"This invitation expired. Ask the person who invited you to send a new one.",
	},
	CANCELED: {
		label: "Canceled",
		color: "default",
		helperText: "This invitation was canceled.",
	},
	INVALID: {
		label: "Invalid",
		color: "default",
		helperText: "This invitation is no longer valid.",
	},
	REJECTED: {
		label: "Rejected",
		color: "error",
		helperText: "This invitation was declined by the FundsyFi admin team.",
	},
};

export const getInvitationStatusMeta = (status: string): InvitationStatusMeta =>
	STATUS_META[status] ?? {
		label: status,
		color: "default",
		helperText: "",
	};

export const isActionableInvitation = (status: string): boolean =>
	status === "PENDING_INVITEE_ACTION";

/** Accepted before verifying: the invitee still has a step to take. */
export const isAwaitingVerification = (status: string): boolean =>
	status === "ACCEPTED_AWAITING_VERIFICATION";

/** Invitations that still need something from the invitee ("Needs response" tab). */
export const needsInviteeAction = (status: string): boolean =>
	isActionableInvitation(status) || isAwaitingVerification(status);
